import * as React from 'react';
import { useState, useMemo } from 'react';

import { Label, IconButton, Dialog, DialogType, Stack, TextField, Dropdown, DialogFooter, PrimaryButton, MessageBar, MessageBarType } from '@fluentui/react';

import { PATIENT_TYPE } from '../../config/constants';
import { Patient } from '../../api/patients.types';
import { updateSecondaryContact } from '../../api/patients';
import { formatPhoneNumber } from '../../util/PhoneNumber';

interface Props {
  patient: Patient;
  onUpdated?: (patient: Patient) => void;
  hideLabel?: boolean;
}

export const SecondaryContact = ({ patient, onUpdated, hideLabel = false }: Props): JSX.Element => {
  const [openDialog, setOpenDialog] = useState(false);
  const { secondaryContact } = patient;
  const relationship = PATIENT_TYPE.find((item) => item.key === secondaryContact?.relationshipId);
  const formattedPhone = secondaryContact ? formatPhoneNumber(secondaryContact.phone) : '';

  const onSave = (data: Patient) => {
    setOpenDialog(false);
    if (onUpdated) {
      onUpdated(data);
    }
  };

  return (
    <Stack>
      {!hideLabel && (
        <Stack horizontal>
          <Label>Secondary Contact</Label>
          <IconButton iconProps={{ iconName: 'Edit' }} onClick={() => setOpenDialog(true)} />
        </Stack>
      )}
      {!secondaryContact && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>None</div>
          {hideLabel && <IconButton iconProps={{ iconName: 'Edit' }} onClick={() => setOpenDialog(true)} />}
        </div>
      )}
      {secondaryContact && (
        <div>
          {secondaryContact.firstName} {secondaryContact.lastName}
          {(relationship && ' (' + relationship.text + ')') || ''}
          {hideLabel && <IconButton iconProps={{ iconName: 'Edit' }} onClick={() => setOpenDialog(true)} />}
          <br />
          {formattedPhone}
        </div>
      )}
      {openDialog && <Edit patient={patient} close={() => setOpenDialog(false)} save={onSave} />}
    </Stack>
  );
};

const Edit = ({ patient, close, save }: { patient: Patient; close: () => void; save: (data: Patient) => void }) => {
  const [error, setError] = useState();
  const [firstName, setFirstName] = useState(patient.secondaryContact?.firstName || '');
  const [lastName, setLastName] = useState(patient.secondaryContact?.lastName || '');
  const [phone, setPhone] = useState(patient.secondaryContact?.phone || '');
  const [relationshipId, setRelationshipId] = useState(patient.secondaryContact?.relationshipId || '');

  const disabled = useMemo(() => {
    return !firstName.trim() || !lastName.trim() || !phone.trim() || !relationshipId;
  }, [firstName, lastName, phone, relationshipId]);

  const onSave = () => {
    setError(undefined);
    updateSecondaryContact(patient.patientId, { firstName, lastName, phone, relationshipId })
      .then((result) => save(result))
      .catch((error) => setError(error.message));
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Secondary Contact',
      }}
      modalProps={{
        isBlocking: true,
      }}
      minWidth={600}
    >
      <Stack tokens={{ childrenGap: 8 }}>
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        <TextField required={true} label="First name" value={firstName} onChange={(e, v) => setFirstName(v!)} />
        <TextField required={true} label="Last name" value={lastName} onChange={(e, v) => setLastName(v!)} />
        <TextField required={true} label="Phone" value={phone} onChange={(e, v) => setPhone(v!)} />
        <Dropdown
          onChange={(e, o) => setRelationshipId(o!.key as string)}
          options={PATIENT_TYPE.map((m) => ({ key: m.key, text: m.text }))}
          disabled={false}
          required={true}
          selectedKey={relationshipId}
          label="Relationship"
        />
      </Stack>

      <DialogFooter>
        <PrimaryButton disabled={disabled} text="Save" onClick={onSave} />
      </DialogFooter>
    </Dialog>
  );
};
